/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import useDownloader from 'react-use-downloader';
import { ranchApi } from '../api';

const docList = ['KYCDoc', 'landDoc', 'AgreementsDoc', 'managementsPlanDoc', 'otherDoc'];

const docVsNameMapping = {
  KYCDoc: 'KYC Documents',
  landDoc: 'Land Documents',
  AgreementsDoc: 'Agreement Documents',
  managementsPlanDoc: 'Management Documents',
  otherDoc: 'Other Documents'
};

const DocumentPreview = ({ viewURL }) => {
  const [loadError, setLoadError] = useState(false);
  const isPDF = viewURL?.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|bmp)$/i.test(viewURL);
  const isDoc =
    /\.(doc|docx|ppt|pptx|xls|xlsx|txt|csv)$/i.test(viewURL) ||
    viewURL?.includes('application/msword');

  let content;
  if (!viewURL) {
    content = <Typography>Loading...</Typography>;
  } else if (loadError || (!isPDF && !isImage && !isDoc)) {
    const isImage = /(\.jpg|\.jpeg|\.png|\.gif|\.bmp)/i.test(viewURL);
    content = isImage ? (
      <img
        src={viewURL}
        alt="Preview"
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        onError={() => setLoadError(true)}
      />
    ) : (
      <iframe
        src={viewURL}
        title="Document"
        width="100%"
        height="100%"
        style={{ border: 'none' }}
      />
    );
  } else {
    content = isImage ? (
      <img
        src={viewURL}
        alt="Preview"
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        onError={() => setLoadError(true)}
      />
    ) : (
      <DocViewer
        documents={[{ uri: viewURL }]}
        pluginRenderers={DocViewerRenderers}
        style={{ height: '80vh', width: '100%' }}
        onError={() => setLoadError(true)}
        config={{
          header: {
            disableHeader: false,
            disableFileName: false,
            retainURLParams: true
          }
        }}
      />
    );
  }

  return <>{content}</>;
};

const DownloadSvgIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 20H19M12 4V16M12 16L7 11M12 16L17 11"
      stroke="#1D1D1F"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ViewSvgIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M14 3v2h3.59L10 12.59 11.41 14 19 6.41V10h2V3h-7zM5 5v14h14v-5h-2v3H7V7h3V5H5z"
      fill="#1d1d1f"
    />
  </svg>
);

export const DocumentCards = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [viewURL, setViewURL] = useState('');
  const [downloadingFile, setDownloadingFile] = useState('');
  const { size, elapsed, percentage, download, cancel, isInProgress, error } = useDownloader();

  // Function to format the upload date
  const formatUploadDate = (dateString) => {
    if (!dateString) return 'Date not available';

    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to sort documents by createdAt date (newest first)
  const sortDocumentsByDate = (documents) => {
    if (!documents || !Array.isArray(documents)) return [];

    return [...documents].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB - dateA; // Descending order (newest first)
    });
  };

  const handleDownload = async (fileDetails, index) => {
    try {
      setDownloadingFile(fileDetails.fileName);
      const { loc, fileName } = fileDetails;
      const locArray = loc.split('/');
      const response = await ranchApi.post('/uploadUrl', {
        folder: locArray[0],
        expiryTime: 300,
        getObject: true,
        key: loc
      });

      const downloadURL = response.data.uploadURL;
      download(downloadURL, fileName);
    } catch (err) {
      console.log('Failed to download.', error);
    }
  };

  const handleCancel = () => {
    cancel();
    setDownloadingFile('');
    alert('Download canceled');
  };

  const handleView = async (fileDetails) => {
    try {
      const { loc } = fileDetails;
      const locArray = loc.split('/');
      const response = await ranchApi.post('/uploadUrl', {
        folder: locArray[0],
        expiryTime: 300,
        getObject: true,
        key: loc
      });
      setViewURL(response.data.uploadURL);
      setOpen(true);
    } catch (error) {
      console.log('error in viewing the file.', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setViewURL('');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
      <Stack spacing={3}>
        {docList?.map((ele, index) => (
          <Stack key={index} spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {docVsNameMapping[ele]}
            </Typography>

            {!data?.[ele] ? (
              <Typography color="text.secondary">No Documents Uploaded</Typography>
            ) : (
              // Sort documents before mapping
              sortDocumentsByDate(data[ele]).map((fileItem, index) => (
                <Stack key={`${index}`} direction="column" spacing={1}>
                  {/* Single container with space-between */}
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                  >
                    <Stack direction="column" spacing={0.5}>
                      <Typography color="#1d1d1f">{fileItem.fileName}</Typography>

                      {/* Upload Date Display - Now below the file name */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '12px',
                          fontStyle: 'italic'
                        }}
                      >
                        Uploaded: {formatUploadDate(fileItem.createdAt)}
                      </Typography>
                    </Stack>

                    {/* Buttons on right */}
                    <Stack direction="row" spacing={2}>
                      <Button
                        onClick={() => handleDownload(fileItem)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 0,
                          padding: '4px 8px',
                          fontSize: 14,
                          color: '#1d1d1f',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#ECECEC'
                          }
                        }}
                      >
                        <DownloadSvgIcon />
                        <span style={{ marginLeft: '4px' }}>Download</span>
                      </Button>

                      <Button
                        onClick={() => handleView(fileItem)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 0,
                          padding: '4px 8px',
                          fontSize: 14,
                          color: '#1d1d1f',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#ECECEC'
                          }
                        }}
                      >
                        <ViewSvgIcon />
                        <span style={{ marginLeft: '4px' }}>View</span>
                      </Button>
                    </Stack>
                  </Stack>

                  {/* Download progress section */}
                  {isInProgress && downloadingFile === fileItem.fileName + index && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={percentage} />
                      <Typography variant="body2">Progress: {percentage.toFixed(0)}%</Typography>
                      <Typography variant="body2">Elapsed Time: {elapsed}s</Typography>
                      <Typography variant="body2">Size: {formatFileSize(size)}</Typography>

                      <Button
                        onClick={handleCancel}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ maxWidth: '150px' }}
                      >
                        Cancel Download
                      </Button>

                      {error && (
                        <Alert severity="error">Download error: {JSON.stringify(error)}</Alert>
                      )}
                    </Stack>
                  )}
                </Stack>
              ))
            )}
          </Stack>
        ))}
      </Stack>

      {/* View Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Document Viewer
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh' }}>
          <DocumentPreview viewURL={viewURL} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
